"use client"

import { useRef, useState } from "react"
import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { ChevronRightIcon, PhoneIcon, ShoppingCartIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { cn } from "@workspace/ui/lib/utils"

gsap.registerPlugin(useGSAP)

/** How long each message holds before the next one, in seconds. */
const ROTATE_S = 6

/**
 * Promo strip atop the `default` and `classic` headers: the tenant's own promo
 * lines (`staffPromoLines` — `promoMessage`, then `promotions.messages`) with
 * an optional "Call us" link at the reading end. Callers render it only when
 * there is at least one line.
 *
 * One line is static. Several rotate every {@link ROTATE_S}s, holding while
 * hovered or focused (incl. focusing the next button), with a next button but
 * no pause button (removed at the client's request, AMI-41); under reduced
 * motion nothing autoplays and the next button steps through them. Every line
 * sits in the same grid cell, so the bar is as tall as the longest one and
 * never jumps as they change; a long line wraps to two lines, then clamps.
 * Lines are plain text — React escapes them.
 */
export function PromoBar({
  messages,
  phone,
  callUsLabel,
}: {
  messages: string[]
  phone: string | undefined
  callUsLabel: string | undefined
}) {
  const t = useTranslations("promoBar")
  const rootRef = useRef<HTMLDivElement>(null)
  const holdRef = useRef(false)
  const shownRef = useRef(0)

  const [index, setIndex] = useState(0)
  // Flipped on only under `prefers-reduced-motion: no-preference`.
  const [motion, setMotion] = useState(false)

  const rotates = messages.length > 1
  const autoplays = rotates && motion
  const active = index % Math.max(1, messages.length)

  useGSAP(
    () => {
      if (!rotates) return
      gsap.matchMedia().add("(prefers-reduced-motion: no-preference)", () => {
        setMotion(true)
        return () => setMotion(false)
      })
    },
    { dependencies: [rotates] }
  )

  // Re-armed on every change (`active` in the deps), so a "next" click gets a
  // full hold too. A held tick waits another round instead of advancing.
  useGSAP(
    () => {
      if (!autoplays) return
      const tick = gsap.delayedCall(ROTATE_S, () => {
        if (holdRef.current) tick.restart(true)
        else setIndex((i) => (i + 1) % messages.length)
      })
    },
    {
      dependencies: [autoplays, messages.length, active],
      revertOnUpdate: true,
    }
  )

  // The incoming line rises into place. Compared against the last index (not
  // a first-run flag) so a StrictMode re-run doesn't animate the SSR'd line.
  useGSAP(
    () => {
      if (shownRef.current === active) return
      shownRef.current = active
      gsap.matchMedia().add("(prefers-reduced-motion: no-preference)", () => {
        gsap.fromTo(
          "[data-promo-active]",
          { opacity: 0, y: 6 },
          { opacity: 1, y: 0, duration: 0.35, ease: "power2.out" }
        )
      })
    },
    { scope: rootRef, dependencies: [active] }
  )

  const hold = (on: boolean) => {
    holdRef.current = on
  }

  return (
    <div className="bg-primary text-xs text-primary-foreground">
      {/* Wraps the "Call us" link onto its own row before the lines get
          squeezed below ~14rem on narrow phones. */}
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-1.5">
        <div
          ref={rootRef}
          role={rotates ? "region" : undefined}
          aria-roledescription={rotates ? "carousel" : undefined}
          aria-label={rotates ? t("label") : undefined}
          className="flex min-w-0 grow basis-56 items-center gap-1.5 font-medium"
          onPointerEnter={() => hold(true)}
          onPointerLeave={() => hold(false)}
          onFocus={() => hold(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) hold(false)
          }}
        >
          <ShoppingCartIcon className="size-3.5 shrink-0" aria-hidden />
          <div
            // Polite only while nothing autoplays, so a rotating strip never
            // talks over the page.
            aria-live={rotates && !autoplays ? "polite" : "off"}
            className="grid min-w-0 flex-1"
          >
            {messages.map((message, i) => (
              <span
                key={i}
                data-promo-active={i === active ? "" : undefined}
                aria-hidden={i === active ? undefined : true}
                title={message}
                className={cn(
                  "line-clamp-2 break-words [grid-area:1/1]",
                  i !== active && "invisible"
                )}
              >
                {message}
              </span>
            ))}
          </div>
          {rotates ? (
            <div className="flex shrink-0 items-center">
              <button
                type="button"
                onClick={() => setIndex((i) => (i + 1) % messages.length)}
                aria-label={t("next")}
                className="inline-flex size-6 items-center justify-center rounded-full hover:bg-primary-foreground/15 focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:outline-none"
              >
                <ChevronRightIcon
                  className="size-3.5 rtl:rotate-180"
                  aria-hidden
                />
              </button>
            </div>
          ) : null}
        </div>
        {phone ? (
          <a
            href={`tel:${phone}`}
            className="inline-flex shrink-0 items-center gap-1.5 font-medium hover:underline"
          >
            <PhoneIcon className="size-3.5" aria-hidden />
            {callUsLabel}
          </a>
        ) : null}
      </div>
    </div>
  )
}
